import { useEffect, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { http } from '@/lib/api';
import { ArrowLeft, Heart, CheckCircle2, Sparkles, MessageCircleHeart, RefreshCw, BookOpen } from 'lucide-react';

const STYLE_ACCENT = {
  secure: 'bg-green/10 text-green',
  anxious: 'bg-gold/15 text-gold',
  avoidant: 'bg-navy/10 text-navy',
  'fearful-avoidant': 'bg-red-50 text-red-700',
};

export default function AttachmentResults() {
  const nav = useNavigate();
  const loc = useLocation();
  const [data, setData] = useState(loc.state || null);
  const [loading, setLoading] = useState(!loc.state);

  useEffect(() => {
    if (data) return;
    http.get('/attachment/latest').then(r => {
      if (!r.data.result) { nav('/app/attachment'); return; }
      setData(r.data);
    }).catch(()=>nav('/app/attachment')).finally(()=>setLoading(false));
  }, [data, nav]);

  if (loading || !data) return <div className="p-10 text-navy/60">Loading…</div>;

  const { result, profile, cool_down_active, days_until_retake, ai_advice } = data;
  const style = result?.style || profile?.style;
  const max = result?.anxiety_max || 16;

  return (
    <div className="p-6 lg:px-10 lg:pt-10 pb-24 space-y-6 lg:space-y-8 lg:max-w-4xl lg:mx-auto" data-testid={`attach-results-${style}`}>
      <button onClick={()=>nav('/app/progress')} data-testid="results-back" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy">
        <ArrowLeft size={14}/> Back to Progress
      </button>

      <div className="bg-navy text-white rounded-3xl p-8 lg:p-12 card-soft">
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Your attachment style</p>
        <h1 className="font-heading text-3xl lg:text-5xl mt-2">{profile.title}</h1>
        <p className="text-white/75 text-sm lg:text-lg mt-4 leading-relaxed">{profile.summary}</p>
        <div className="grid grid-cols-2 gap-3 lg:gap-5 mt-6">
          <div className="bg-white/10 rounded-2xl p-4">
            <p className="text-xs uppercase tracking-widest text-white/60">Anxiety axis</p>
            <p className="font-heading text-2xl lg:text-3xl mt-1" data-testid="score-anxiety">{result.anxiety_score}<span className="text-sm text-white/40"> / {max}</span></p>
            <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-gold" style={{ width: `${Math.max(0, Math.min(100, ((result.anxiety_score + max) / (max * 2)) * 100))}%` }}/>
            </div>
          </div>
          <div className="bg-white/10 rounded-2xl p-4">
            <p className="text-xs uppercase tracking-widest text-white/60">Avoidance axis</p>
            <p className="font-heading text-2xl lg:text-3xl mt-1" data-testid="score-avoidance">{result.avoidance_score}<span className="text-sm text-white/40"> / {max}</span></p>
            <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-gold" style={{ width: `${Math.max(0, Math.min(100, ((result.avoidance_score + max) / (max * 2)) * 100))}%` }}/>
            </div>
          </div>
        </div>
      </div>

      {ai_advice && (
        <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft" data-testid="ai-advice">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={18} className="text-gold"/>
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Personalized guidance</p>
          </div>
          <div className="max-w-none text-navy/85 leading-relaxed space-y-4" data-testid="ai-advice-text">
            {ai_advice.split(/\n{2,}/).map((block, bi) => {
              const boldMatch = block.match(/^\*\*(.+?)\*\*\s*$/);
              if (boldMatch) {
                return <p key={bi} className="font-heading text-lg lg:text-xl text-navy mt-1">{boldMatch[1]}</p>;
              }
              // Render inline **bold** if present
              const parts = block.split(/(\*\*[^*]+\*\*)/g);
              return (
                <p key={bi} className="text-sm lg:text-base leading-relaxed whitespace-pre-wrap">
                  {parts.map((p, pi) => {
                    const m = p.match(/^\*\*(.+)\*\*$/);
                    return m ? <strong key={pi} className="font-semibold text-navy">{m[1]}</strong> : <span key={pi}>{p}</span>;
                  })}
                </p>
              );
            })}
          </div>
          <p className="text-[11px] text-navy/40 mt-4">Generated by AI to reflect your specific pattern. Not therapy, not a diagnosis.</p>
        </div>
      )}

      {cool_down_active && (
        <div className="bg-beige rounded-3xl p-5 lg:p-6 flex items-start gap-3" data-testid="cooldown-banner">
          <RefreshCw size={18} className="text-gold shrink-0 mt-0.5"/>
          <div>
            <p className="font-heading text-base lg:text-lg">Reassessment available in {days_until_retake} day{days_until_retake===1?'':'s'}</p>
            <p className="text-sm text-navy/60 mt-1">Attachment shifts slowly. We space assessments 30 days apart so you can see meaningful change.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft" data-testid="strengths">
          <p className="font-heading text-lg lg:text-xl mb-3 flex items-center gap-2"><CheckCircle2 size={18} className="text-green"/> Strengths</p>
          <ul className="space-y-2">
            {profile.strengths.map((s,i) => (
              <li key={s || i} className="flex items-start gap-2 text-sm lg:text-base leading-relaxed"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green shrink-0"/><span>{s}</span></li>
            ))}
          </ul>
        </div>
        <div className="bg-beige rounded-3xl p-6 lg:p-8" data-testid="growth-areas">
          <p className="font-heading text-lg lg:text-xl mb-3 flex items-center gap-2"><Sparkles size={18} className="text-gold"/> Growth areas</p>
          <ul className="space-y-2">
            {profile.growth_areas.map((s,i) => (
              <li key={s || i} className="flex items-start gap-2 text-sm lg:text-base leading-relaxed"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gold shrink-0"/><span>{s}</span></li>
            ))}
          </ul>
        </div>
      </div>

      <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft" data-testid="relationship-patterns">
        <p className="font-heading text-lg lg:text-xl mb-3">Relationship patterns</p>
        <ul className="space-y-2 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0">
          {profile.relationship_patterns.map((s,i) => (
            <li key={s || i} className="flex items-start gap-2 text-sm lg:text-base leading-relaxed"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-navy shrink-0"/><span>{s}</span></li>
          ))}
        </ul>
      </div>

      <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft" data-testid="reflection">
        <p className="font-heading text-lg lg:text-xl mb-3">Reflection</p>
        <p className="text-base lg:text-lg italic text-navy/85 leading-relaxed">&quot;{profile.reflection}&quot;</p>
      </div>

      <div className="bg-navy text-white rounded-3xl p-6 lg:p-8 card-soft" data-testid="action-plan">
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Action plan</p>
        <p className="font-heading text-xl lg:text-2xl mt-2">Small moves that build a more secure you.</p>
        <ul className="space-y-3 mt-4">
          {profile.action_plan.map((s,i) => (
            <li key={s || i} className="flex items-start gap-3 text-sm lg:text-base leading-relaxed">
              <CheckCircle2 size={16} className="text-gold shrink-0 mt-0.5"/>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft" data-testid="recommended-lessons">
        <p className="font-heading text-lg lg:text-xl mb-3 flex items-center gap-2"><BookOpen size={18} className="text-navy"/> Recommended lessons</p>
        <p className="text-sm text-navy/60 mb-4">Curated for your style.</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
          {profile.recommended_lessons.map(lid => (
            <Link key={lid} to={`/app/learn/lesson/${lid}`} data-testid={`rec-${lid}`}
              className="block bg-beige rounded-2xl px-4 py-3 hover:bg-[#EAE5D9] transition-smooth">
              <p className="text-sm lg:text-base font-medium">Open lesson</p>
              <p className="text-xs text-navy/50 mt-0.5">{lid}</p>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
        <Link to="/app/support/coach" className="block bg-white rounded-3xl p-6 lg:p-8 card-soft hover:-translate-y-0.5 transition-smooth" data-testid="cta-support-coach">
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Talk it through</p>
          <p className="font-heading text-lg lg:text-xl mt-2 flex items-center gap-2"><MessageCircleHeart size={18}/> Support Coach</p>
          <p className="text-sm text-navy/60 mt-1">Clinical, culturally responsive guidance for supporting yourself and others.</p>
        </Link>
        <Link to="/app/coach" className="block bg-white rounded-3xl p-6 lg:p-8 card-soft hover:-translate-y-0.5 transition-smooth" data-testid="cta-ai-coach">
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Talk to your coach</p>
          <p className="font-heading text-lg lg:text-xl mt-2 flex items-center gap-2"><Heart size={18}/> AI Coach</p>
          <p className="text-sm text-navy/60 mt-1">A wise older brother in your corner — for you, personally.</p>
        </Link>
      </div>

      <p className="text-xs text-navy/40 text-center pt-4">
        Not diagnostic. This is a supportive reflection tool. If patterns are causing distress, consider working with a licensed therapist.
      </p>
    </div>
  );
}
